import React, { useState, useEffect } from 'react';
import { TextField, Popper, ClickAwayListener, Button, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { textFieldSX } from '../../constants/textFieldCSS';
import { useGetOneShotSamplesByNodeId, useCreateOneShotSample, useRemoveOneShotSampleFromNodeRun, useRemoveOneShotSampleFromId } from '../../api/oneShotSampleApi';
import { OneShotSample } from '../../types/oneShotSample';

interface Sample {
    id?: string;
    userMessage: string;
    outputContent: string;
}

interface OneShotSamplesPopperProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    width?: number | string;
    nodeId: string;
    workflowId: string;
}

const OneShotSamplesPopper: React.FC<OneShotSamplesPopperProps> = ({
    open,
    anchorEl,
    onClose,
    width = '50%',
    nodeId,
    workflowId,
}) => {
    const [userMessage, setUserMessage] = useState('');
    const [outputContent, setOutputContent] = useState('');
    const [showForm, setShowForm] = useState(false);
    
    const { data: existingSamples = [], refetch } = useGetOneShotSamplesByNodeId(nodeId);
    const createSampleMutation = useCreateOneShotSample();
    const removeSampleMutation = useRemoveOneShotSampleFromNodeRun();
    const removeSampleByIdMutation = useRemoveOneShotSampleFromId();

    // Find the FloatingWorkflowBoxPanel element to use as anchor
    const [panelAnchor, setPanelAnchor] = useState<HTMLElement | null>(null);
    
    useEffect(() => {
        // Find the floating panel when the popper opens
        if (open) {
            const panel = document.querySelector('.floating-workflow-panel') as HTMLElement;
            setPanelAnchor(panel);
        }
    }, [open]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (userMessage.trim() && outputContent.trim()) {
            const sample: Partial<OneShotSample> = {
                node_id: nodeId,
                workflow_id: workflowId,
                input: JSON.stringify({ user_message: userMessage.trim() }),
                output: JSON.stringify({ content: outputContent.trim() }),
                unique_id: `${nodeId}-${workflowId}-${Date.now()}`,
            };

            try {
                await createSampleMutation.mutateAsync(sample as OneShotSample);
                setUserMessage('');
                setOutputContent('');
                refetch();
                setShowForm(false);
            } catch (error) {
                console.error('Failed to create one-shot sample:', error);
            }
        }
    };

    const handleDelete = async (sampleId: string | undefined) => {
        if (!sampleId) return;
        try {
            await removeSampleByIdMutation.mutateAsync({ id: sampleId });
            refetch();
        } catch (error) {
            console.error('Failed to delete one-shot sample:', error);
        }
    };

    const oneShotSamples = existingSamples.map((sample: OneShotSample) => {
        let inputJson = JSON.parse(sample.input || '{}');
        let outputJson = JSON.parse(sample.output || '{}');

        return {
            id: sample.id,
            userMessage: inputJson.user_message,
            outputContent: outputJson.content,
        }
    });

    return (
        <Popper
            open={open}
            anchorEl={panelAnchor || anchorEl}
            placement="left-start"
            style={{ 
                maxWidth: '50%',
                width: 'auto',
                zIndex: 1300,
                marginRight: '16px'
            }}
            modifiers={[
                {
                    name: 'offset',
                    options: {
                        offset: [200, 10],
                    },
                },
            ]}
        >
            <ClickAwayListener onClickAway={onClose}>
                <div className="bg-white rounded shadow-lg border border-borderGrey p-4 max-h-[90vh] overflow-y-auto overflow-x-hidden">
                    <div className="flex justify-between items-center mb-4">
                        <div className="font-medium">One-Shot Samples</div>
                        {!showForm && (
                            <Button 
                                variant="outlined" 
                                size="small"
                                onClick={() => setShowForm(true)}
                                className="!normal-case !text-nanonetsBlue !border-nanonetsBlue"
                            >
                                Add Sample
                            </Button>
                        )}
                    </div>
                    {/* Form */}
                    {showForm && (
                        <div className="mb-6 rounded flex justify-end">
                            <div className="bg-backgroundGrey p-4 space-y-4 w-[60%]">
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="User Message"
                                    value={userMessage}
                                    onChange={(e) => setUserMessage(e.target.value)}
                                    sx={textFieldSX}
                                    className="bg-white rounded"
                                    variant="outlined"
                                />
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Output Content"
                                    value={outputContent}
                                    onChange={(e) => setOutputContent(e.target.value)}
                                    sx={textFieldSX}
                                    className="bg-white rounded"
                                    variant="outlined"
                                />
                                <div className="flex justify-end space-x-2">
                                    <Button 
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setShowForm(false)}
                                        className="!normal-case !text-nanonetsBlue !border-nanonetsBlue"
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        variant="contained"
                                        size="small"
                                        onClick={handleSubmit}
                                        className="!bg-nanonetsBlue !normal-case !text-white"
                                    >
                                        Add Sample
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    <div className="border rounded overflow-x-auto">
                        <table className="w-full border-collapse table-fixed">
                            <thead>
                                <tr className="bg-backgroundGrey">
                                    {Object.keys(oneShotSamples[0] || {}).filter(key => key !== 'id').map((key) => (
                                        <th 
                                            key={key} 
                                            className="p-3 text-left border-b font-medium text-sm break-words"
                                            style={{ width: '45%' }}
                                        >
                                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                        </th>
                                    ))}
                                    <th className="p-3 text-left border-b font-medium text-sm" style={{ width: '10%' }}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {oneShotSamples.map((sample) => (
                                    <tr key={sample.id} className="hover:bg-backgroundGrey">
                                        {Object.keys(sample).filter(key => key !== 'id').map((key) => (
                                            <td 
                                                key={key} 
                                                className="p-3 border-b align-top break-words text-sm"
                                                style={{ width: '45%' }}
                                            >
                                                {sample[key as keyof Sample]}
                                            </td>
                                        ))}
                                        <td className="p-3 border-b align-top text-center" style={{ width: '10%' }}>
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleDelete(sample.id)}
                                                className="text-gray-500 hover:text-red-500"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                                {oneShotSamples.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="p-3 text-center text-gray-500">
                                            No samples added yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </ClickAwayListener>
        </Popper>
    );
};

export default OneShotSamplesPopper;
